(() => {
  const namespace = 'FenixEvergreenVideoHero';

  if (window[namespace]) {
    window[namespace].init(document);
    return;
  }

  const selector = '[data-evergreen-video-hero]';
  const controllers = new Map();

  const matchingRoots = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches(selector)) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll(selector));
    return roots;
  };

  const setControlState = (control, playing) => {
    control.dataset.playing = playing ? 'true' : 'false';
    control.setAttribute('aria-label', playing ? 'Pause background video' : 'Play background video');
  };

  const setup = (root) => {
    if (controllers.has(root)) return;

    const video = root.querySelector('video');
    const control = root.querySelector('[data-video-control]');
    if (!video || !control) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection;
    let inView = false;
    let hydrated = false;
    let userPaused = false;
    let observer;

    const hydrate = () => {
      if (hydrated) return;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.load();
      hydrated = true;
      root.dataset.videoHydrated = 'true';
    };

    const pause = () => {
      video.pause();
      setControlState(control, false);
    };

    const automaticMotionAllowed = () => {
      const slowConnection = ['slow-2g', '2g'].includes(connection?.effectiveType);
      return !reducedMotion.matches && !connection?.saveData && !slowConnection;
    };

    const play = async (explicit = false) => {
      if (!explicit && (!automaticMotionAllowed() || userPaused || !inView || document.hidden)) return;
      hydrate();
      video.defaultMuted = true;
      video.muted = true;
      video.playsInline = true;
      try {
        await video.play();
        setControlState(control, true);
      } catch (error) {
        setControlState(control, false);
      }
    };

    const onControlClick = () => {
      if (video.paused) {
        userPaused = false;
        play(true);
      } else {
        userPaused = true;
        pause();
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        pause();
      } else {
        play();
      }
    };

    const onMotionChange = () => {
      if (reducedMotion.matches) {
        pause();
      } else {
        play();
      }
    };

    const onConnectionChange = () => {
      if (automaticMotionAllowed()) {
        play();
      } else {
        pause();
      }
    };

    const onPlay = () => setControlState(control, true);
    const onPause = () => setControlState(control, false);

    control.addEventListener('click', onControlClick);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('error', onPause);
    document.addEventListener('visibilitychange', onVisibilityChange);
    reducedMotion.addEventListener('change', onMotionChange);
    connection?.addEventListener?.('change', onConnectionChange);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting && entry.intersectionRatio >= .25;
          if (inView) {
            play();
          } else {
            pause();
          }
        });
      }, { threshold: [0, .25, .75] });
      observer.observe(root);
    } else {
      inView = true;
      play();
    }

    root.dataset.jsReady = 'true';
    setControlState(control, false);

    controllers.set(root, {
      destroy() {
        observer?.disconnect();
        pause();
        control.removeEventListener('click', onControlClick);
        video.removeEventListener('play', onPlay);
        video.removeEventListener('pause', onPause);
        video.removeEventListener('error', onPause);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        reducedMotion.removeEventListener('change', onMotionChange);
        connection?.removeEventListener?.('change', onConnectionChange);
        controllers.delete(root);
      }
    });
  };

  const init = (scope = document) => matchingRoots(scope).forEach(setup);
  const destroy = (scope) => matchingRoots(scope).forEach((root) => controllers.get(root)?.destroy());

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));

  window[namespace] = { init, destroy };
  init(document);
})();
